"use client";

import { useCallback, useRef } from "react";
import { useTranslations } from "next-intl";
import { ArrowRight } from "lucide-react";
import { capturePostHogEvent } from "@/lib/posthog";
import { readHomeVariant } from "@/lib/home-variant";
import Image from "next/image";
import { useImpression } from "@/hooks/use-impression";
import { useCookieInterest } from "@/contexts/CookieInterestContext";

/**
 * Shot supplied by the owner, background replaced with the tile tint so the
 * cookies read as a product cut-out rather than as someone else's photograph
 * of a wooden table. One constant, so swapping in a photo of OUR cookies once
 * they exist is a single-line change.
 */
const COOKIE_IMAGE = "/images/cookie-pretotype.webp";

/** Mirrors CakeCard's `sizes`, since the tile occupies an identical grid cell. */
const COOKIE_IMAGE_SIZES = "(min-width: 1024px) 350px, (min-width: 640px) 320px, 180px";

/**
 * Cookie demand pretotype — a fake-door tile sitting inline among the cakes.
 *
 * We do not sell cookies and have not built them. This tile exists to answer
 * one question before any of that investment happens: do the people already
 * shopping for our cakes want cookies enough to act?
 *
 * Why it lives in the grid rather than on a landing page or in a survey:
 *
 *  - It is seen at the moment of shopping intent, by people who are already
 *    choosing what to buy from us. A survey asks a hypothetical; this asks the
 *    same person, in the same breath as a real purchase decision.
 *  - It gets a concurrent control for free. The custom-cake tile beside it is
 *    now impression-tracked too, so cookie interest can be read against a live
 *    neighbour instead of against a stale before/after.
 *
 * Note on the benchmark: this tile carries a real photograph, so it competes on
 * the same terms as a cake card and the CAKE CARDS are now the fair comparison
 * (6.94% CTR, position-matched). While it was a flat illustration the fair
 * comparison was the photo-less custom tile. Read both, but weight the cards.
 *
 * Honesty: nothing here implies cookies are purchasable. The tile is badged
 * "coming soon" and the dialog it opens says plainly that they are not for
 * sale yet. That is a deliberate design choice, not a hedge — an email left
 * by someone who has just been told they cannot buy is far stronger evidence
 * than a click by someone who thinks they can.
 *
 * Hidden in variant A by CSS so the control arm keeps today's markup exactly;
 * `display` is left to globals.css for that reason (see CustomCakeTile).
 */
export function CookieTile() {
  const t = useTranslations("cookiePretotype");
  const { openCookieInterest } = useCookieInterest();
  const tileRef = useRef<HTMLButtonElement>(null);

  // Built at fire time, never during render: `readHomeVariant` reads a DOM
  // attribute stamped by a pre-paint script, so calling it while rendering
  // would differ between server and client and trip hydration.
  const buildEventProperties = useCallback(
    () => ({
      placement: "grid_tile",
      surface: "homepage",
      home_variant: readHomeVariant(),
    }),
    [],
  );

  useImpression(
    tileRef,
    useCallback(() => {
      capturePostHogEvent("cookie_tile_impression", buildEventProperties());
    }, [buildEventProperties]),
  );

  const handleClick = () => {
    capturePostHogEvent("cookie_tile_clicked", buildEventProperties());
    openCookieInterest();
  };

  return (
    <button
      ref={tileRef}
      type="button"
      onClick={handleClick}
      data-cookie-tile=""
      // Photo-shaped, unlike the icon-led custom tile: the question is whether
      // cookies are wanted as a *product*, so the tile has to compete for the
      // eye on the same terms a cake card does. An icon-sized mark next to
      // full-bleed cake photography would measure the icon, not the cookies.
      className="cake-cookie-tile group relative flex-col self-stretch overflow-hidden text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      {/*
        Structure mirrors a cake card exactly — square image, then a compact
        body — and the matching styling lives in globals.css beside the card
        rules rather than in Tailwind utilities here.

        The height parity is not cosmetic. These are CSS grid items, so a tile
        that renders taller than its row-mates stretches the entire row and
        pads every real cake card beside it with dead space. The first build of
        this tile was 269px against the cards' 222px and did exactly that.

        Two body lines, not three: at 390px a cake card gives its body ~51px,
        which is one title line plus one footer line. The explanatory sentence
        that used to sit between them is folded into the call to action, so the
        tile asks its question in the same space a cake card states its price.
      */}
      <span className="cookie-tile-img relative block aspect-square w-full flex-shrink-0 overflow-hidden">
        <Image
          src={COOKIE_IMAGE}
          alt={t("tileImageAlt")}
          fill
          sizes={COOKIE_IMAGE_SIZES}
          quality={75}
          // Below the fold on every breakpoint, exactly like the cake cards
          // that are not first in the grid — so it must not compete with the
          // LCP image for bandwidth.
          loading="lazy"
          className="object-cover"
        />
        <span className="cookie-tile-badge">{t("tileBadge")}</span>
      </span>
      <span className="cookie-tile-body flex flex-col">
        <span className="line-clamp-1 text-sm font-semibold text-foreground sm:text-base">
          {t("tileTitle")}
        </span>
        <span className="cookie-tile-cta mt-auto inline-flex items-center gap-1">
          {t("tileCta")}
          <ArrowRight
            className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </span>
      </span>
    </button>
  );
}
